import Flex from '@Styles/flex.scss';
import { Dialog } from '@mui/material';
import cx from 'classnames';
import React from 'react';
import { useSelector } from 'react-redux';
import Button, { getBtnColor } from '@Components/Button/Button';
import ModalBody from '@Components/ModalComponents/ModalBody';
import ModalFooter, {
    ModalFooterContainer,
} from '@Components/ModalComponents/ModalFooter';
import ModalHeader from '@Components/ModalComponents/ModalHeader';
import { BrowserPersistenceApi } from '@Data/browserPersistence/Api';
import { selectedFolderIdState } from '@Data/folders/Reducer';

export enum ConfirmQcDialogType {
    COMPLETE,
    QUIT,
}

export interface IConfirmDialogButtonProps {
    text: string;
    color: string;
}

interface IConfirmDialogProps {
    isOpen: boolean;
    onClose: () => void;
    onConfirm: () => void;
    dialogType: ConfirmQcDialogType;
    isSubmitting: boolean;
}

export const ConfirmDialog = (props: IConfirmDialogProps) => {
    const { isOpen, onClose, onConfirm, dialogType, isSubmitting } = props;
    const selectedFolderId = useSelector(selectedFolderIdState);

    let title, body, footer, width;

    if (dialogType === ConfirmQcDialogType.COMPLETE) {
        title = 'Submit QC Results?';
        body = <span>Submit Results</span>;

        footer = (
            <ModalFooter
                onCancel={onClose}
                onSubmit={onConfirm}
                submitText="Submit Results"
                cancelText="Cancel"
                isSubmitting={isSubmitting}
            />
        );
    } else {
        width = '800px';
        title = 'Stop QC Inspection?';

        body = (
            <div>
                <p>Do you want to stop the current QC inspection?</p>
                <p>
                    Selecting "Pause" will allow you to pick up this QC
                    Inspection at a later date. Selecting "Cancel" will remove
                    any saved data.
                </p>
            </div>
        );

        const handleCancelRun = () => {
            BrowserPersistenceApi.removePersistedQcRun(selectedFolderId);
            onConfirm();
        };

        footer = (
            <ModalFooterContainer>
                <Button
                    size="small"
                    color="info"
                    variant="contained"
                    onClick={onClose}
                    disabled={isSubmitting}
                    role="modal-footer-cancel-btn"
                    sx={{
                        backgroundColor: getBtnColor('black'),
                        fontWeight: '400',
                        fontSize: '14px',
                        letterSpacing: '.02em',
                        lineHeight: '17px',
                        padding: '4px 10px',
                        '&:hover': {
                            backgroundColor: getBtnColor('black', true),
                        },
                    }}
                >
                    Continue
                </Button>

                <div
                    className={cx(Flex.flex, Flex.alignCenter)}
                    style={{ gap: '10px' }}
                >
                    <Button
                        size="small"
                        color="info"
                        variant="contained"
                        onClick={onConfirm}
                        disabled={isSubmitting}
                        sx={{
                            backgroundColor: getBtnColor('green'),
                            fontWeight: '400',
                            fontSize: '14px',
                            letterSpacing: '.02em',
                            lineHeight: '17px',
                            padding: '4px 10px',
                            '&:hover': {
                                backgroundColor: getBtnColor('green', true),
                            },
                        }}
                    >
                        Pause
                    </Button>
                    <Button
                        size="small"
                        color="info"
                        variant="contained"
                        onClick={handleCancelRun}
                        disabled={isSubmitting}
                        sx={{
                            backgroundColor: getBtnColor('red'),
                            fontWeight: '400',
                            fontSize: '14px',
                            letterSpacing: '.02em',
                            lineHeight: '17px',
                            padding: '4px 10px',
                            '&:hover': {
                                backgroundColor: getBtnColor('red', true),
                            },
                        }}
                    >
                        Cancel
                    </Button>
                </div>
            </ModalFooterContainer>
        );
    }

    return (
        <Dialog open={isOpen} maxWidth="sm">
            <ModalHeader title={title} onCancel={onClose} />
            <ModalBody>
                <div style={{ width: width ?? '400px' }}>{body}</div>
            </ModalBody>
            {footer}
        </Dialog>
    );
};
